import { useEffect, useState, useCallback } from "react";
import { Cloud, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

// First-party bridge popup. Salesforce embeds the softphone as a cross-site iframe
// where the session cookie is blocked (third-party). This popup is top-level (first
// party), so its cookie works; it mints a token and postMessages it to the opener
// (the iframe — same origin as this popup), which then uses it as a Bearer token.
export default function SalesforceBridge() {
    const [status, setStatus] = useState("checking"); // checking | need-login | done | error
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const handoff = useCallback(async () => {
        const { data } = await api.get("/auth/bridge-token");
        if (window.opener) {
            window.opener.postMessage({ source: "inspiro-sf-bridge", token: data.token }, window.location.origin);
        }
        setStatus("done");
        setTimeout(() => { try { window.close(); } catch (_e) { /* ignore */ } }, 1400);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                await handoff();
            } catch (_e) {
                setStatus("need-login");
            }
        })();
    }, [handoff]);

    const doLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
            await handoff();
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || "Sign in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-paper p-6" data-testid="sf-bridge">
            <div className="w-full max-w-xs space-y-4 text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#00A1E0]/10 text-[#00A1E0]">
                    <Cloud size={26} weight="fill" />
                </div>

                {status === "checking" && (
                    <p className="text-sm text-stone2" data-testid="sf-bridge-checking">Linking your softphone…</p>
                )}

                {status === "done" && (
                    <div className="space-y-2" data-testid="sf-bridge-done">
                        <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-500" />
                        <div className="font-heading text-lg font-bold">Softphone connected</div>
                        <p className="text-sm text-stone2">You can close this window and return to Salesforce.</p>
                    </div>
                )}

                {status === "need-login" && (
                    <form onSubmit={doLogin} className="space-y-3 text-left" data-testid="sf-bridge-login">
                        <div className="text-center font-heading text-lg font-bold">Sign in to connect</div>
                        <input
                            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="Work email" data-testid="sf-bridge-email"
                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                        />
                        <input
                            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password" data-testid="sf-bridge-password"
                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                        />
                        {err && <p className="text-xs text-red-600" data-testid="sf-bridge-error">{err}</p>}
                        <button
                            type="submit" disabled={busy} data-testid="sf-bridge-submit"
                            className="w-full rounded-full bg-ink py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {busy ? "Signing in…" : "Sign in & connect"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
